import React from 'react';
import { useSelector } from 'react-redux';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import {
  trackHeroArticle,
  trackMostPopularArticle,
  trackArticle,
  trackHeroVideoArticle,
  trackArticleFeedShowMore,
  trackArticleFeedTab,
  trackViewMoreLink,
} from '../../common/analytics/analyticsTracker';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import config from '../../common/config';
import { AdUnits } from '../../common/helpers/ads/dfp';
import { fetchNewsLandingPageData } from '../../common/helpers/data/landingPages/fetchNewsGuidesDecorLandingPageData';
import { Content } from '../../common/helpers/data/landingPages/fetchPostsData';
import { fetchLandingPageTopContentData } from '../../common/helpers/data/landingPages/fetchTopContentData';
import {
  getLandingPageType,
  LandingPageTypes,
} from '../../common/helpers/domain/landingPages';
import {
  getSubcategories,
  PostMainCategory,
  PostSubcategory,
} from '../../common/helpers/domain/postCategories';
import logger from '../../common/helpers/logger/logger';
import { extractPostIds } from '../../components/articleFeed/articleFeedHelper';
import { getNewsTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { Breadcrumb } from '../../components/global/breadcrumb/Breadcrumb';
import { PostList } from '../../components/global/PostList/PostList';
import HeroArticle from '../../components/homePage/heroArticle/HeroArticle';
import HomeSeoHeader from '../../components/homePage/homeSeoHeader/HomeSeoHeader';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import { generatePostMainCategoryBreadcrumbList } from '../../components/landingPage/breadcrumb';
import PostListModule from '../../components/landingPage/PostListModule';
import BillboardAd from '../../components/landingPageAds/BillboardAd';
import MainTitle from '../../components/mainTitle/MainTitle';
import MostPopularPosts from '../../components/mostPopular/MostPopularPosts';
import {
  DynamicComponentForArticleFeedModule,
  DynamicComponentForFeaturedVideos,
  DynamicComponentForHeroVideo,
} from '../../components/pages/homePage/DynamicComponents';
import {
  AsideContainer,
  LeftContainer,
  TopContentWrapper,
} from '../../components/pages/homePage/HomePage.style';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import Row from '../../components/rows/Row';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import ViewMoreLink from '../../components/ViewMoreLink/ViewMoreLink';
import Page from '../../layouts/main';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import commonResource from '../../locales/th/common.json';
import homeResource from '../../locales/th/home.json';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { HeroVideoInfo } from '../../queries/homePage/heroVideo';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { updatePageType } from '../../store/home/slice';
import { updateCurrentSourcePage } from '../../store/search/slice';
import { WholeState } from '../../store/common/type';
import { getAdSectByPageType } from '../../common/helpers/ads/AdsHelper';

interface NewsLandingPageProps {
  canonicalLink: string;
  heroArticle: HeroArticleInfo;
  popularArticles: MostPopular[];
  generalNewsPosts: Content[];
  propertyNewsPosts: Content[];
  heroVideo: HeroVideoInfo;
  featuredVideos: Content[];
}

function NewsLandingPage({
  canonicalLink,
  heroArticle,
  popularArticles,
  propertyNewsPosts,
  generalNewsPosts,
  heroVideo,
  featuredVideos,
}: NewsLandingPageProps) {
  const moduleResource = commonResource.postModules.news;
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const landingPageType = LandingPageTypes.NewsPage;
  const analyticsSource = generateEventSourceByPageType(landingPageType);

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <HomeSeoHeader pageType={landingPageType} canonicalLink={canonicalLink} />
      <Breadcrumb
        items={generatePostMainCategoryBreadcrumbList(PostMainCategory.News)}
      />
      <Row verticalSpacing={0} maxWidth={homePageRowSetting.maxWidth}>
        <MainTitle
          title={homeResource.title.news}
          subtitle={homeResource.subtitle.news}
        />
      </Row>
      <Row
        verticalSpacing={0}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <TopContentWrapper>
          <LeftContainer>
            <HeroArticle
              heroArticleData={heroArticle}
              onClickHeroArticle={(heroArticle) =>
                trackHeroArticle(
                  userInfo,
                  heroArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_ARTICLE,
                )
              }
            />
          </LeftContainer>
          <AsideContainer>
            <MostPopularPosts
              post={popularArticles}
              onClick={(popularArticle) =>
                trackMostPopularArticle(
                  userInfo,
                  popularArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.POPULAR,
                )
              }
            />
          </AsideContainer>
        </TopContentWrapper>
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard1}
          sect={getAdSectByPageType(landingPageType)}
        />
      </Row>
      {propertyNewsPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="news"
        >
          <PostListModule
            title={moduleResource.propertyNews.title}
            subtitle={moduleResource.propertyNews.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType([
                  PostSubcategory.PropertyNews,
                ])}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    targetLandingPage,
                    ANALYTICS_FEATURES.LATEST_PROPERTY_NEWS,
                  );
                }}
              />
            }
          >
            <PostList
              contents={propertyNewsPosts}
              adUnit={AdUnits.HP_Pos1}
              adSect={getAdSectByPageType(landingPageType)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.LATEST_PROPERTY_NEWS,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {(heroVideo || featuredVideos.length > 0) && (
        <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
          <div style={{ overflowX: 'hidden' }}>
            <DynamicComponentForHeroVideo
              heroVideo={heroVideo}
              onClick={(heroVideo) =>
                trackHeroVideoArticle(
                  userInfo,
                  heroVideo,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_VIDEO,
                )
              }
            />
            <DynamicComponentForFeaturedVideos
              contents={featuredVideos}
              onClickVideoCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.VIDEO,
                )
              }
            />
          </div>
        </Row>
      )}
      {generalNewsPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="news"
        >
          <PostListModule
            title={moduleResource.generalNews.title}
            subtitle={moduleResource.generalNews.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType([
                  PostSubcategory.GeneralNews,
                ])}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    targetLandingPage,
                    ANALYTICS_FEATURES.LATEST_GENERAL_NEWS,
                  );
                }}
              />
            }
          >
            <PostList
              contents={generalNewsPosts}
              adUnit={AdUnits.HP_Pos2}
              adSect={getAdSectByPageType(landingPageType)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.LATEST_GENERAL_NEWS,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={homeResource.readMore.title} />
        <DynamicComponentForArticleFeedModule
          tabDefinition={getNewsTabDefinition()}
          excludePostIds={extractPostIds(
            [...propertyNewsPosts, ...generalNewsPosts, ...featuredVideos],
            heroVideo,
          )}
          adUnit={AdUnits.HP_MrecInfiniteAd}
          adSect={getAdSectByPageType(landingPageType)}
          onClickPostCard={(post) =>
            trackArticle(
              userInfo,
              post,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            )
          }
          onClickArticleFeedShowMore={() => {
            trackArticleFeedShowMore(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            );
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackArticleFeedTab(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
              selectedTab,
            );
          }}
        />
      </Row>
      <Row verticalSpacing={16} fullWidth>
        <SeoFooter />
      </Row>
    </Page>
  );
}

NewsLandingPage.getInitialProps = async ({
  store,
  asPath,
  apolloClient,
}: CustomNextPageContext): Promise<NewsLandingPageProps> => {
  const canonicalLink = config.baseUrl + asPath.substring(1);
  try {
    const { heroArticle, mostPopular } = await fetchLandingPageTopContentData(
      apolloClient,
      {
        postCategories: getSubcategories(PostMainCategory.News),
      },
    );

    const { propertyNews, generalNews, video } = await fetchNewsLandingPageData(
      apolloClient,
      heroArticle?.id ? [heroArticle.id] : [],
    );

    const landingPageType = getLandingPageType(
      getSubcategories(PostMainCategory.News),
    );
    store.dispatch(updatePageType(landingPageType));
    store.dispatch(
      updateCurrentSourcePage(generateEventSourceByPageType(landingPageType)),
    );

    return {
      canonicalLink,
      heroArticle,
      propertyNewsPosts: propertyNews,
      generalNewsPosts: generalNews,
      popularArticles: mostPopular,
      heroVideo: video.heroVideo,
      featuredVideos: video.featuredVideos,
    };
  } catch (e) {
    logger.logError(
      'NewsLandingPage.getInitialProps',
      'Error fetching data',
      e,
    );
  }
};

export default NewsLandingPage;
